import { useEffect } from "react";

// Inject a JSON-LD <script> for the lifetime of the component (removed on unmount).
// `data` may be a single object or an array of schema.org objects.
export function useJsonLd(id, data) {
  useEffect(() => {
    if (!data) return undefined;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    el.textContent = JSON.stringify(data);
    document.head.appendChild(el);
    return () => { el.remove(); };
  }, [id, data]);
}

// Lightweight per-route SEO: syncs <title>, meta description, canonical and the
// main Open Graph / Twitter tags with the current page (image optional).
// Restores previous values on unmount.
export function useSeo(title, description, path, image) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;

    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta ? meta.getAttribute("content") : null;
    if (meta && description) meta.setAttribute("content", description);

    const canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical ? canonical.getAttribute("href") : null;
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const prevOgTitle = ogTitle ? ogTitle.getAttribute("content") : null;
    const ogUrl = document.querySelector('meta[property="og:url"]');
    const prevOgUrl = ogUrl ? ogUrl.getAttribute("content") : null;
    const ogDesc = document.querySelector('meta[property="og:description"]');
    const prevOgDesc = ogDesc ? ogDesc.getAttribute("content") : null;
    const ogImg = document.querySelector('meta[property="og:image"]');
    const prevOgImg = ogImg ? ogImg.getAttribute("content") : null;
    const twImg = document.querySelector('meta[name="twitter:image"]');
    const prevTwImg = twImg ? twImg.getAttribute("content") : null;
    const twTitle = document.querySelector('meta[name="twitter:title"]');
    const prevTwTitle = twTitle ? twTitle.getAttribute("content") : null;
    const twDesc = document.querySelector('meta[name="twitter:description"]');
    const prevTwDesc = twDesc ? twDesc.getAttribute("content") : null;

    const url = window.location.origin + (path || window.location.pathname);
    if (canonical) canonical.setAttribute("href", url);
    if (ogTitle) ogTitle.setAttribute("content", title);
    if (ogUrl) ogUrl.setAttribute("content", url);
    if (ogDesc && description) ogDesc.setAttribute("content", description);
    if (twTitle) twTitle.setAttribute("content", title);
    if (twDesc && description) twDesc.setAttribute("content", description);
    if (image) {
      if (ogImg) ogImg.setAttribute("content", image);
      if (twImg) twImg.setAttribute("content", image);
    }

    return () => {
      document.title = prevTitle;
      if (meta && prevDesc) meta.setAttribute("content", prevDesc);
      if (canonical && prevCanonical) canonical.setAttribute("href", prevCanonical);
      if (ogTitle && prevOgTitle) ogTitle.setAttribute("content", prevOgTitle);
      if (ogUrl && prevOgUrl) ogUrl.setAttribute("content", prevOgUrl);
      if (ogDesc && prevOgDesc) ogDesc.setAttribute("content", prevOgDesc);
      if (ogImg && prevOgImg) ogImg.setAttribute("content", prevOgImg);
      if (twImg && prevTwImg) twImg.setAttribute("content", prevTwImg);
      if (twTitle && prevTwTitle) twTitle.setAttribute("content", prevTwTitle);
      if (twDesc && prevTwDesc) twDesc.setAttribute("content", prevTwDesc);
    };
  }, [title, description, path, image]);
}

// Build the absolute URL of the dynamic Open Graph image for a given city/title.
const OG_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
export function ogImageUrl(params) {
  if (!OG_BASE) return undefined;
  const q = new URLSearchParams(params).toString();
  return `${OG_BASE}/api/og-image?${q}`;
}
